import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { scrollbar } from '@helpers/scrollbar';
import { horizontalFadeIn, staggerChildren, verticalFadeIn } from '@helpers/variants';
import FilterSort from '@stories/Elements/ProductFilters/FilterSort/FilterSort';
import { FilterSortStyles } from '@stories/Elements/ProductFilters/FilterSort/FilterSort.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import { defaultPadding } from '@stories/Settings/Grids/Grid.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';

const Wrapper = styled.div`
  --wrapperWidth: ${fluid(300, 375, Device.TabletLarge, Device.ActualDesktop)};
  min-width: var(--wrapperWidth);
  max-width: var(--wrapperWidth);
  width: 100%;
  z-index: 2;
  transition: top 0.15s ease-in-out;

  @media ${until(Device.TabletLarge)} {
    background-color: ${brand.grey[95]};
    position: sticky;
    top: var(--topOffset);
  }
`;

const Container = styled(m.div).attrs(() => ({
  initial: 'hidden',
  animate: 'visible',
  variants: verticalFadeIn(25),
}))`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 68px;
  transition: top 0.15s ease-in-out;

  [data-compare-open='true'] & {
    top: 125px;

    @media ${from(Device.TabletLarge)} {
      top: 240px;
    }
  }

  height: max-content;
  max-height: 100%;
`;

const TopBar = styled.div`
  background-color: ${brand.red.base};
  ${fonts.helvetica['63-medium-extended']};
  color: ${brand.white};
  text-transform: uppercase;
  display: flex;
  position: relative;
  padding: 15px 25px;
  font-size: 15px;
  justify-content: center;
  user-select: none;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const MobileTopBar = styled(TopBar)`
  font-size: 17px;
  background-color: ${brand.black};

  span {
    margin: auto;
  }

  display: none;
  @media ${until(Device.TabletLarge)} {
    display: flex;
  }
`;

const TopBarIcon = styled.div`
  position: absolute;
  top: 13px;
  left: 13px;
  width: 30px;
`;

const FiltersInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px 20px;
  background-color: ${brand.white};
  gap: 30px;

  @media ${from(Device.TabletLarge)} {
    transition: max-height 0.15s ease-in-out;
    overflow: auto;
    ${scrollbar(brand.white, brand.white, brand.red.base, '6px', false)}
    &::-webkit-scrollbar-thumb {
      border: none;
      border-radius: 0px;
    }
  }
`;

const ThemedFilterSort = styled(FilterSort)`
  border-left: 0;
  border-right: 0;
  height: 100%;

  @media ${from(Device.TabletLarge)} {
    border-left: 1px solid ${brand.grey[80]};
    border-right: 1px solid ${brand.grey[80]};
  }

  transition: opacity 0.25s ease-in-out;
  [disabled='true'] & {
    opacity: 0.5;
    pointer-events: none;
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: row;
    z-index: 1001;

    ${FilterSortStyles.Select} {
      justify-content: center;
    }
  }

  ${Typography.PStyle};
  @media ${from(Device.TabletLarge)} {
    ${FilterSortStyles.Title} {
      ${fonts.helvetica['73-bold-extended']};
    }

    ${FilterSortStyles.Select} {
      justify-content: flex-end;
      ${fonts.helvetica['73-bold-extended']};
    }

    ${FilterSortStyles.List} {
      width: 100%;
      left: 0px;
    }

    padding: 10px 15px;
  }
`;

const FiltersList = styled(m.div).attrs(() => ({
  initial: 'hidden',
  animate: 'visible',
  variants: staggerChildren(0.15),
}))`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const FilterWrapper = styled(m.div).attrs(() => ({
  variants: horizontalFadeIn(-25),
}))``;

const DesktopLayout = styled.div`
  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const MobileLayout = styled.div`
  ${defaultPadding};

  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 2px solid ${brand.grey[85]};

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;

const MobileFilterButton = styled.div`
  display: flex;
  text-align: center;
  align-items: center;
  ${fonts.helvetica['93-black-extended']};
  font-size: 16px;
  text-transform: uppercase;
  cursor: pointer;

  span {
    flex-grow: 1;
  }

  svg:first-of-type {
    width: 35px;
  }

  svg:last-of-type {
    width: 16px;
  }
`;

const MobileFiltersInner = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const MobileFiltersContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// eslint-disable-next-line
const MobileFilters = styled(m.div).attrs((props: any) => ({
  initial: 'hidden',
  animate: !props['data-visible'] ? 'visible' : 'hidden',
  exit: 'hidden',
  variants: {
    hidden: {
      height: '0',
    },
    visible: {
      height: `calc(100dvh - ${props['data-mobile-nav-offset'] ?? '0'}px)`,
    },
  },
  transition: {
    duration: 0.35,
  },
}))<{ 'data-mobile-nav-offset'?: number }>`
  position: fixed;
  top: ${(props) => props['data-mobile-nav-offset'] ?? '0'}px;
  [data-compare-open='true'] & {
    top: calc(${(props) => props['data-mobile-nav-offset'] ?? '0'}px + 100px);

    ${() => MobileFiltersInner} {
      max-height: calc(100dvh - (${(props) => props['data-mobile-nav-offset'] ?? '0'}px + 100px));
    }
  }
  left: 0;
  width: 100vw;
  z-index: 1001;
  overflow: auto;

  @media ${from(Device.TabletLarge)} {
    pointer-events: none;
    display: none;
  }

  background-color: ${brand.white};

  &[data-visible='false'] {
    pointer-events: none;
  }
`;

const MobileClose = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  position: absolute;
  right: 13px;

  ${fonts.helvetica['45-light']};
  font-size: 13px;
  text-transform: uppercase;

  svg {
    transform: rotate(45deg);
    width: 20px;
    height: 20px;
  }
`;

const TopFilters = styled.div<{ mobileFiltersOpen?: boolean }>`
  ${defaultPadding};
  background-color: ${brand.grey[95]};
  display: flex;
  border-top: 1px solid ${brand.grey[80]};
  border-bottom: 1px solid ${brand.grey[80]};
  color: ${brand.grey[35]};

  @media ${until(Device.TabletLarge)} {
    ${({ mobileFiltersOpen }) =>
      mobileFiltersOpen &&
      css`
        display: none;
      `}
    min-height: 60px;
  }
`;

const ResetCompare = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 20px;
  padding-left: 20px;
  padding-right: 20px;
  border-left: 1px solid ${brand.grey[80]};
  border-right: 1px solid ${brand.grey[80]};

  padding-top: 10px;
  padding-bottom: 10px;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }

  min-width: ${fluid(300, 375, Device.TabletLarge)};
`;

const ResetButton = styled.button`
  display: flex;
  border: none;
  align-items: center;
  cursor: pointer;
  gap: 15px;
  color: ${brand.grey[35]};

  transition: opacity 0.25s ease-in-out;
  &[disabled='true'] {
    opacity: 0.5;
    pointer-events: none;
  }

  ${Typography.PStyle};
  ${fonts.helvetica['73-bold-extended']};

  text-transform: uppercase;

  svg {
    height: 20px;
  }
`;

const FiltersElement = styled.div`
  position: sticky;
  top: 0px;
  z-index: 40;
  transition: top 0.15s ease-in-out;

  &[data-compare-open='true'] {
    top: 100px;

    @media ${from(Device.TabletLarge)} {
      top: 172px;
    }
  }
`;

const MobileDropdownIcon = styled.div`
  svg {
    position: absolute;
    right: 18px;
    width: 16px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const MobileFiltersWrapper = styled.div`
  width: 50%;
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  ${fonts.helvetica['93-black-extended']};
  font-size: 16px;
  cursor: pointer;
  text-transform: uppercase;

  ${() => TopBarIcon} {
    left: unset;
    top: unset;
    position: relative;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;

const SortByWrapper = styled.div`
  margin-left: auto;

  ${FilterSortStyles.Wrapper} {
    justify-content: center;
    height: 100%;
  }

  @media ${until(Device.TabletLarge)} {
    width: 50%;

    span {
      display: none;
    }
  }
`;

const TagList = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`;

const Tag = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  font-size: 13px;
  border: 2px solid ${brand.black};

  transition: opacity 0.25s ease-in-out;
  [data-disabled='true'] & {
    opacity: 0.5;
    pointer-events: none;
  }

  svg {
    width: 10px;
  }
`;

export const ProductListingFiltersStyles = {
  Container,
  TopBar,
  TopBarIcon,
  FiltersInner,
  ThemedFilterSort,
  FiltersList,
  FilterWrapper,
  Wrapper,
  DesktopLayout,
  MobileLayout,
  MobileFilterButton,
  MobileFilters,
  MobileTopBar,
  MobileClose,
  MobileFiltersInner,
  MobileFiltersContent,
  TopFilters,
  ResetCompare,
  FiltersElement,
  ResetButton,
  SortByWrapper,
  TagList,
  Tag,
  MobileFiltersWrapper,
  MobileDropdownIcon,
};
